import { useMutation, useQuery } from "@tanstack/react-query"
import GameScreenLayout from "@/components/GameScreenLayout"
import { attackWorldBoss, getWorldBossActive } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { usePlayerGpsState } from "@/hooks/usePlayerGpsState"
import { formatDistanceM } from "@/utils/formatDistance"
import { distanceMeters } from "@/utils/geo"
import { displayCardBS } from "@/constants/game.constants"

export default function WorldBossPage() {
  const gps = usePlayerGpsState()
  const { passport, collectionCards, syncGameState, bumpCards } =
    useGuardiansPassport()

  const q = useQuery({
    queryKey: ["world-boss"],
    queryFn: getWorldBossActive,
    refetchInterval: 30_000,
  })

  const activeId =
    passport?.activeCardId ?? passport?.activeCard?.id ?? collectionCards[0]?.id

  const boss = q.data?.event ?? null
  const dist =
    boss && gps
      ? distanceMeters(gps.lat, gps.lng, boss.latitude, boss.longitude)
      : null
  const inRange =
    boss && gps != null && dist != null && dist <= boss.radiusMeters

  const attack = useMutation({
    mutationFn: () => {
      if (!boss || !gps || !activeId) throw new Error("Нужны босс, GPS и карта")
      return attackWorldBoss({
        eventId: boss.id,
        cardId: activeId,
        playerLat: gps.lat,
        playerLng: gps.lng,
      })
    },
    onSuccess: async () => {
      await syncGameState()
      bumpCards()
      void q.refetch()
    },
  })

  return (
    <GameScreenLayout title="Мировой босс" backTo="/play/map">
      {q.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : !boss ? (
        <p className="text-sm text-guardians-muted">Сейчас нет активного события.</p>
      ) : (
        <div className="space-y-4">
          <section className="rounded-xl border border-white/10 p-4">
            <h2 className="text-lg font-semibold">{boss.name}</h2>
            <p className="text-sm text-guardians-muted">
              HP {boss.currentHp.toLocaleString("ru-RU")} / {boss.maxHp.toLocaleString("ru-RU")}
            </p>
            <p className="text-xs text-guardians-muted">Статус: {boss.status}</p>
            {gps && dist != null ? (
              <p className="mt-2 text-xs">
                Расстояние: {formatDistanceM(dist)}
                {inRange ? " · в радиусе" : " · подойдите ближе"}
              </p>
            ) : null}
          </section>

          {activeId ? (
            <p className="text-xs text-guardians-muted">
              Карта: BS{" "}
              {displayCardBS(
                collectionCards.find((c) => c.id === activeId) ?? collectionCards[0]!
              )}
            </p>
          ) : null}

          <button
            type="button"
            disabled={!inRange || !activeId || attack.isPending || boss.status !== "ACTIVE"}
            onClick={() => attack.mutate()}
            className="w-full rounded-xl bg-guardians-accent py-3 text-sm font-medium text-guardians-bg disabled:opacity-40"
          >
            Атаковать
          </button>

          {boss.leaderboard.length ? (
            <section>
              <h3 className="mb-2 text-sm font-medium">Топ урона</h3>
              <ol className="space-y-1 text-sm">
                {boss.leaderboard.slice(0, 10).map((row, i) => (
                  <li key={row.userId} className="flex justify-between">
                    <span>
                      {i + 1}. {row.nickname}
                    </span>
                    <span className="text-guardians-accent">{row.damage}</span>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>
      )}

      {attack.isError ? (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(attack.error, "Атака не удалась")}
        </p>
      ) : null}

      {attack.data ? (
        <div className="mt-4 rounded-xl border border-green-500/30 p-3 text-sm">
          Урон: {attack.data.effectiveDamage} (было HP {attack.data.bossHpBefore} →{" "}
          {attack.data.bossHpAfter})
        </div>
      ) : null}
    </GameScreenLayout>
  )
}
